import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { isNil, Nullable } from '@sibe/utilities/common';
import {
  HsClickControlAction,
  HsControlActionType,
  HsControlActionsHandler,
  HsKeyboardControlAction,
  HsMouseMoveControlAction,
  HsScrollControlAction,
  HsVector2,
  type HsControlAction,
} from '@sibe/hyperscene';
import { fromEvent, Subscription, timer } from 'rxjs';
import { subscribeOutsideAngular } from '../../functions/rxjs-operators/subscribe-outside-angular.operator';

@Component({
  selector: 'hyperscene-user-events',
  templateUrl: './user-actions.component.html',
  styleUrls: ['./user-actions.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserActionsComponent implements AfterViewInit, OnDestroy {
  private readonly controlActionsHandler: HsControlActionsHandler = new HsControlActionsHandler();

  private readonly controlActionDescriptionsMap: Map<HsControlActionType, string> = new Map([
    [HsControlActionType.LeftClick, 'Mouse Click or touch Tap'],

    [HsControlActionType.LeftMove, 'Move mouse with pressed left button or move by touch Pan'],
    [HsControlActionType.RightMove, 'Move mouse with pressed right button'],
    [HsControlActionType.MiddleMove, 'Move mouse with pressed middle button'],

    [HsControlActionType.Scroll, 'Mouse Scroll or touch Pinch'],

    [HsControlActionType.PressedTop, 'Press ArrowUp or W key'],

    [HsControlActionType.PressedBottom, 'Press ArrowDown or S key'],

    [HsControlActionType.PressedLeft, 'Press ArrowLeft or A key'],

    [HsControlActionType.PressedRight, 'Press ArrowRight or D key'],

    [HsControlActionType.PressedPlus, 'Press + key'],
    [HsControlActionType.PressedMinus, 'Press - key'],

    [HsControlActionType.PressedCtrl, 'Press Control key'],
    [HsControlActionType.PressedShift, 'Press Shift key'],

    [HsControlActionType.PressedAdditionalOne, 'Press Q key'],
    [HsControlActionType.PressedAdditionalTwo, 'Press E key'],
    [HsControlActionType.PressedEscape, 'Press Esc key'],
    [HsControlActionType.PressedBackspace, 'Press Backspace key'],
  ]);

  public readonly controlActions: Map<HsControlActionType, HsControlAction> = this.filterActions();

  public readonly keyControlActionTypeByKeyBoardKeyMap: Map<string, HsControlActionType> = new Map([
    ['ArrowUp', HsControlActionType.PressedTop],
    ['w', HsControlActionType.PressedTop],

    ['ArrowDown', HsControlActionType.PressedBottom],
    ['s', HsControlActionType.PressedBottom],

    ['ArrowLeft', HsControlActionType.PressedLeft],
    ['a', HsControlActionType.PressedLeft],

    ['ArrowRight', HsControlActionType.PressedRight],
    ['d', HsControlActionType.PressedRight],

    ['+', HsControlActionType.PressedPlus],
    ['-', HsControlActionType.PressedMinus],

    ['Control', HsControlActionType.PressedCtrl],
    ['Shift', HsControlActionType.PressedShift],
    ['Escape', HsControlActionType.PressedEscape],
    ['Backspace', HsControlActionType.PressedBackspace],

    ['q', HsControlActionType.PressedAdditionalOne],
    ['e', HsControlActionType.PressedAdditionalTwo],
  ]);

  private mouseButton: Nullable<number> = null;

  private frameId: number = 0;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.subscription.add(this.handleMouseDown());
    this.subscription.add(this.handleMouseUp());
    this.subscription.add(this.handleMouseMove());
    this.subscription.add(this.handleScroll());
    this.subscription.add(this.handleKeydown());
    this.subscription.add(this.handleKeyup());
  }

  public ngAfterViewInit(): void {
    requestAnimationFrame(() => this.processControlActions(this.controlActionsHandler.controlActionsMap));
  }

  public ngOnDestroy(): void {
    window.cancelAnimationFrame(this.frameId);
    this.subscription.unsubscribe();
  }

  public onTap(event: HammerInput): void {
    const position: HsVector2 = new HsVector2(event.center.x, event.center.y);
    const clickControlAction: HsClickControlAction = new HsClickControlAction(
      HsControlActionType.LeftClick,
      position,
      true
    );
    this.controlActionsHandler.updateControlAction(clickControlAction);
  }

  public onPan(event: HammerInput): void {
    const delta: HsVector2 = new HsVector2(event.deltaX, event.deltaY);
    const mouseMoveControlAction: HsMouseMoveControlAction = new HsMouseMoveControlAction(
      HsControlActionType.LeftMove,
      delta
    );
    this.controlActionsHandler.updateControlAction(mouseMoveControlAction);
  }

  public onPinch(event: HammerInput): void {
    const position: HsVector2 = new HsVector2(event.center.x, event.center.y);
    const scrollControlAction: HsScrollControlAction = new HsScrollControlAction(position, event.deltaY);
    this.controlActionsHandler.updateControlAction(scrollControlAction);
  }

  public getActionDescription(controlActionType: HsControlActionType): string {
    return this.controlActionDescriptionsMap.get(controlActionType) ?? '';
  }

  public processControlActions(controlActionsMap: Map<HsControlActionType, HsControlAction>): void {
    controlActionsMap.forEach((controlAction: HsControlAction) => {
      if (!controlAction.isActive) {
        return;
      }

      controlAction.setInactive();

      timer(300).subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
    });

    this.frameId = requestAnimationFrame(() =>
      this.processControlActions(this.controlActionsHandler.controlActionsMap)
    );
  }

  private filterActions(): Map<HsControlActionType, HsControlAction> {
    const filteredActions: [HsControlActionType, HsControlAction][] = [
      ...this.controlActionsHandler.controlActionsMap,
    ].filter(
      (controlActionByType: [HsControlActionType, HsControlAction]) =>
        !(
          controlActionByType[0] === HsControlActionType.CameraType ||
          controlActionByType[0] === HsControlActionType.CameraToolType ||
          // Pinch zoom reaches this page as a scroll action, so the zoom action never fires here.
          controlActionByType[0] === HsControlActionType.Zoom
        )
    );
    return new Map(filteredActions);
  }

  private handleMouseDown(): Subscription {
    return fromEvent(this.document, 'mousedown')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => {
        if (event instanceof MouseEvent) {
          this.mouseButton = event.button;
        }
      });
  }

  private handleMouseUp(): Subscription {
    return fromEvent(this.document, 'mouseup')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe(() => {
        this.mouseButton = null;
      });
  }

  private handleMouseMove(): Subscription {
    return fromEvent(this.document, 'mousemove')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => {
        if (!(event instanceof MouseEvent)) {
          return;
        }

        if (isNil(this.mouseButton) || this.mouseButton === 0) {
          return;
        }

        const delta: HsVector2 = new HsVector2(event.x, event.y);
        const moveType: HsControlActionType =
          this.mouseButton === 1 ? HsControlActionType.MiddleMove : HsControlActionType.RightMove;
        const mouseMoveControlAction: HsMouseMoveControlAction = new HsMouseMoveControlAction(moveType, delta);
        this.controlActionsHandler.updateControlAction(mouseMoveControlAction);
        this.changeDetectorRef.detectChanges();
      });
  }

  private handleScroll(): Subscription {
    return fromEvent(this.document, 'mousewheel')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => {
        if (!(event instanceof WheelEvent)) {
          return;
        }

        const position: HsVector2 = new HsVector2(event.x, event.y);
        const scrollControlAction: HsScrollControlAction = new HsScrollControlAction(position, event.deltaY);
        this.controlActionsHandler.updateControlAction(scrollControlAction);
        this.changeDetectorRef.detectChanges();
      });
  }

  private handleKeydown(): Subscription {
    return fromEvent(this.document, 'keydown')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => {
        if (!(event instanceof KeyboardEvent)) {
          return;
        }

        const actionType: Nullable<HsControlActionType> = this.keyControlActionTypeByKeyBoardKeyMap.get(event.key);
        if (isNil(actionType)) {
          return;
        }

        const keyboardControlAction: HsKeyboardControlAction = new HsKeyboardControlAction(actionType, true);
        this.controlActionsHandler.updateControlAction(keyboardControlAction);
        this.changeDetectorRef.detectChanges();
      });
  }

  private handleKeyup(): Subscription {
    return fromEvent(this.document, 'keyup')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => {
        if (!(event instanceof KeyboardEvent)) {
          return;
        }

        const actionType: Nullable<HsControlActionType> = this.keyControlActionTypeByKeyBoardKeyMap.get(event.key);
        if (isNil(actionType)) {
          return;
        }

        const keyboardControlAction: HsKeyboardControlAction = new HsKeyboardControlAction(actionType, false);
        this.controlActionsHandler.updateControlAction(keyboardControlAction);
        this.changeDetectorRef.detectChanges();
      });
  }
}
